type TrackedEvent = {
  readonly event: string;
  readonly page: string;
  readonly section?: string;
  readonly session_id: string;
  readonly props?: Record<string, unknown>;
};

const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? "";
const EVENTS_URL = `${API_BASE}/events`;
const FLUSH_MS = 5000;
const MAX_BUFFER = 50;
const SID_KEY = "rf_sid";
const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

let buffer: TrackedEvent[] = [];
let sessionId = "";
let started = false;
let nativeFetch: typeof fetch | null = null;

function ignoreTelemetryError(_error: unknown): void {
  // 분석 수집 실패는 앱 흐름에 영향을 주지 않는다.
}

function getSessionId(): string {
  if (sessionId) return sessionId;

  const gen = () =>
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  try {
    let stored = sessionStorage.getItem(SID_KEY);
    if (!stored) {
      stored = gen();
      sessionStorage.setItem(SID_KEY, stored);
    }
    sessionId = stored;
  } catch (error) {
    ignoreTelemetryError(error);
    sessionId = gen();
  }

  return sessionId;
}

function currentPage(): string {
  return (window.location.pathname + window.location.search).slice(0, 300);
}

export function track(
  event: string,
  opts?: {
    readonly section?: string;
    readonly props?: Record<string, unknown>;
    readonly page?: string;
  }
): void {
  try {
    buffer.push({
      event,
      page: opts?.page ?? currentPage(),
      section: opts?.section,
      session_id: getSessionId(),
      props: opts?.props,
    });
    if (buffer.length >= MAX_BUFFER) flush();
  } catch (error) {
    ignoreTelemetryError(error);
  }
}

function flush(unload = false): void {
  if (buffer.length === 0) return;

  const events = buffer;
  buffer = [];
  const body = JSON.stringify({ events });

  try {
    if (unload && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(
        EVENTS_URL,
        new Blob([body], { type: "text/plain" })
      );
      return;
    }

    const send = nativeFetch ?? window.fetch.bind(window);
    void send(EVENTS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(ignoreTelemetryError);
  } catch (error) {
    ignoreTelemetryError(error);
  }
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  return (init?.method ?? (input instanceof Request ? input.method : "GET"))
    .toUpperCase();
}

function requestPath(input: RequestInfo | URL): string {
  const raw =
    input instanceof Request
      ? input.url
      : input instanceof URL
        ? input.toString()
        : input;

  try {
    const parsed = new URL(raw, window.location.origin);
    return `${parsed.pathname}${parsed.search}`.slice(0, 300);
  } catch {
    return String(raw).slice(0, 300);
  }
}

function shouldTrackFetch(input: RequestInfo | URL, init?: RequestInit): boolean {
  const method = requestMethod(input, init);
  if (!MUTATION_METHODS.has(method)) return false;
  return !requestPath(input).startsWith("/events");
}

function patchFetch(): void {
  if (nativeFetch || typeof window.fetch !== "function") return;

  nativeFetch = window.fetch.bind(window);
  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const shouldTrack = shouldTrackFetch(input, init);
    const method = requestMethod(input, init);
    const path = requestPath(input);

    try {
      const response = await nativeFetch?.(input, init);
      if (!response) {
        throw new Error("fetch is unavailable");
      }
      if (shouldTrack) {
        track("api.mutation", {
          props: {
            phase: response.ok ? "success" : "failure",
            status: response.status,
            method,
            path,
            tracked_by: "fetch_mutation",
          },
        });
      }
      return response;
    } catch (error) {
      if (shouldTrack) {
        track("api.mutation", {
          props: {
            phase: "failure",
            method,
            path,
            tracked_by: "fetch_mutation",
            error: error instanceof Error ? error.message : "unknown fetch error",
          },
        });
      }
      throw error;
    }
  };
}

function nearestTrackable(el: Element): HTMLElement | null {
  const selector =
    "[data-track],button,a,[role=button],input[type=submit],input[type=button]";
  const found = el.closest(selector);
  return found instanceof HTMLElement ? found : null;
}

function onClick(e: MouseEvent): void {
  try {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const node = nearestTrackable(target);
    if (!node) return;

    const dataTrack = node.getAttribute("data-track") ?? undefined;
    const sectionEl = node.closest("[data-section]");

    track(dataTrack ?? "click", {
      section: sectionEl?.getAttribute("data-section") ?? undefined,
      props: {
        tag: node.tagName.toLowerCase(),
        text: (node.textContent ?? "").trim().slice(0, 80) || undefined,
        id: node.id || undefined,
        data_track: dataTrack,
        aria: node.getAttribute("aria-label") ?? undefined,
        name: node.getAttribute("name") ?? undefined,
      },
    });
  } catch (error) {
    ignoreTelemetryError(error);
  }
}

function patchHistory(): void {
  const fire = () => track("page_view");
  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);

  history.pushState = (...args: Parameters<History["pushState"]>): void => {
    origPush(...args);
    fire();
  };
  history.replaceState = (
    ...args: Parameters<History["replaceState"]>
  ): void => {
    origReplace(...args);
    fire();
  };
  window.addEventListener("popstate", fire);
}

function init(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  document.addEventListener("click", onClick, true);
  patchFetch();
  patchHistory();
  track("page_view");
  window.setInterval(() => flush(), FLUSH_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => flush(true));
}

init();
